import angular from 'angular';
import _ from 'lodash-es';
import moment from 'moment';
import { COMPOSED_OF } from '~/scripts/constants/common-kids';
import APIV5 from '~/models/api';
import Node from '~/scripts/services/model/node';
import Form from '~/scripts/services/model/form';
import Utils from '~/scripts/services/utils';
import { ConfirmDeleteModal } from '~/utils/modals';
import templateModalFormBuilderReference from './modal/modal-form-builder-ref.html';
import templateModalFormBuilderSelect from './modal/modal-form-builder-select.html';
import ModalFormBuilderSelectController from './modal/modal-form-builder-select.controller';

const sortByKeys = (/** @type {any} */ object) => {
	const keys = Object.keys(object);
	const sortedKeys = _.sortBy(keys);

	return Object.fromEntries(
		_.map(sortedKeys, key => {
			let value = object[key] ?? null;
			if (typeof value === 'object' && !(Array.isArray(value)) && value !== null) {
				value = sortByKeys(value);
			}
			return [key, value];
		})
	);
};

export default class FormBuilderController {
	#componentsModified;

	#oldFormComponents;

	/** @type {string | undefined} */
	#oldFormName;

	/** @type {?import('@knitiv/api-client-javascript').KNode} */
	#oldFormTerm;

	/* @ngInject */
	/**
	 *
	 * @param {import('angular').IFilterService} $filter
	 * @param {import('angular').ILocationService} $location
	 * @param {import('angular').route.IRouteParamsService} $routeParams
	 * @param {any} $rootScope
	 * @param {any} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {import('angular').ui.bootstrap.IModalService} $uibModal
	 * @param {import('~/scripts/services/form.service').default} FormService
	 * @param {*} NodeService
	 * @param {*} Notification
	 * @param {*} User
	 * @param {import('angular').auto.IInjectorService} $injector
	 */
	constructor(
		$filter,
		$location,
		$routeParams,
		$rootScope,
		$scope,
		$timeout,
		$translate,
		$uibModal,
		FormService,
		NodeService,
		Notification,
		User,
		$injector,
	) {
		this.$filter = $filter;
		this.$location = $location;
		this.$routeParams = $routeParams;
		this.$rootScope = $rootScope;
		this.$scope = $scope;
		this.$timeout = $timeout;
		this.$translate = $translate;
		this.$uibModal = $uibModal;
		this.FormService = FormService;
		this.NodeService = NodeService;
		this.Notification = Notification;
		this.User = User;
		this.$injector = $injector;

		/** @type {boolean} */
		this.#componentsModified = false;
		this.#oldFormComponents = {};

		this.#oldFormName = undefined;
		this.#oldFormTerm = null;

		/** @type {?import('~/scripts/services/model/node').default} */
		this.class = null;

		/** @type {boolean} */
		this.create = false;

		/** @type {boolean} */
		this.edit = true;

		/** @type {?import('~/scripts/services/model/form').default} */
		this.form = null;

		/** @type {import('@knitiv/api-client-javascript').KNode | undefined} */
		this.formKid = undefined;

		/** @type {import('@knitiv/api-client-javascript').KNode | undefined} */
		this.kid = undefined;

		/** @type {boolean} */
		this.modified = false;

		/** @type {import('~/components/mf/mf.controller').MfOptions} */
		this.searchBarMfOptions = {
			displayExpandButton: false,
			name:                '',
			uniqueKid:           true,
		};

		this.$scope.$on('$locationChangeStart', (
			/** @type {Event} */ event,
			/** @type {string} */ newLocation,
			/** @type {string} */ oldLocation,
		) => {
			const newUrl = new URL(newLocation);
			const oldUrl = new URL(oldLocation);
			if (
				'preventDefault' in event
				&& newUrl
				&& oldUrl
				&& newUrl.pathname !== oldUrl.pathname
				&& this.modified
				&& $rootScope.context?.token
				// eslint-disable-next-line no-alert
				&& !(window.confirm('Attention, you will lost changes! Click OK to confirm'))
			) {
				event.preventDefault();
			}
		});

		this.$rootScope.formBuilderClassName = 'Class test';

		this.$timeout(async () => {
			/** @namespace $routeParams.classId */
			if (this.$routeParams.classId) {
				// New form
				this.#oldFormName = undefined;
				this.#oldFormTerm = null;
				this.#oldFormComponents = {};
				this.create = true;
				this.kid = `K_NODE;${this.User.getRef()}`;
				this.class_kid = this.$routeParams.classId;
				this.modified = true;

				await this.loadClassInfo();

				if ('generic' in this.$location.search()) {
					const result = await this.FormService.loadGeneric(this.class_kid, { rg: false, from: 'builder' });
					if (result) {
						this.form = new Form(this.$injector, result);
					}
				}
			}
			else if (this.$routeParams.formId) {
				// Existing form
				this.kid = this.$routeParams.formId;
				if (this.kid) {
					this.formKid = this.kid;
					await this.loadForm(this.kid);
				}
			}
			else {
				this.searchBarMfOptions.focusSearch = true;
			}

			this.$scope.$watchCollection('$ctrl.form', (
				/** @type {any} */ newValue,
				/** @type {any} */ oldValue,
			) => {
				if (!_.isEqual(newValue, oldValue)) {
					this.modified = true;
				}
			});

			this.$timeout(() => {
				this.$scope.$watch('$ctrl.form.components', (
					/** @type {any} */ newValue,
					/** @type {string | any[]} */ oldValue,
				) => {
					if (!_.isEqual(newValue, oldValue) && Array.isArray(oldValue) && oldValue.length > 0) {
						this.#componentsModified = !_.isEqual(newValue, this.#oldFormComponents);
						if (this.#componentsModified) {
							this.modified = true;
						}
					}
				}, true);
			}, 1000);

			// Posé sur $rootScope, donc à retirer à la main : le contrôleur est recréé
			// à chaque ouverture du générateur de formulaires.
			const offFormBuilderUpdate = this.$rootScope.$on('FormBuilder.update', () => {
				this.modified = true;
			});

			this.$scope.$on('$destroy', () => offFormBuilderUpdate());
		});
	}

	/**
	 * @param {import('@knitiv/api-client-javascript').KNode} formKid
	 */
	createForm(formKid) {
		this.$location.path(`/form-builder/new/${formKid}`);
	}

	/**
	 * @param {any} components
	 * @param {any} map
	 * @param {any[]} path
	 * @returns {boolean}
	 */
	checkFormComponents(components, map = {}, path = []) {
		let pass = true;

		for (const component of Object.values(components)) {
			if ('key' in component) {
				if (component.key in map) {
					pass = false;
					const labelpath = [...path, component.label || component.title || component.type].join(' ▶ ');
					this.Notification.error({
						title:   this.$translate.instant('error.title'),
						message: `${labelpath}<br/>${map[component.key]}<br />Duplicate property name: ${component.key}`,
					});
				}
				else {
					map[component.key] = [...path, component.label || component.title || component.type].join(' ▶ ');
				}
			}

			if (pass) {
				if ('components' in component) {
					path.push(component.label || component.type);
					pass = this.checkFormComponents(component.components, map, path);
					_.remove(path, p => p === component.key);
				}
				else if (component.type === 'table' && 'rows' in component) {
					for (const [kr, row] of component.rows.entries()) {
						for (const [kc, col] of row.entries()) {
							if (pass) {
								const label = `${component.label}, row ${kr + 1}, column ${kc + 1}`;
								path.push(label);
								pass = this.checkFormComponents(col.components, map, path);
								_.remove(path, p => p === label);
							}
						}
					}
				}
				else if (component.type === 'columns' && 'columns' in component) {
					for (const [k, col] of component.columns.entries()) {
						if (pass) {
							const label = `${component.label}, column ${k + 1}`;
							path.push(label);
							pass = this.checkFormComponents(col.components, map, path);
							_.remove(path, p => p === label);
						}
					}
				}
				else if (component.type === 'tabs' && 'tabs' in component) {
					for (const tab of component.tabs) {
						if (pass) {
							const label = `${component.label} ${tab.title}`;
							path.push(`${component.label} ${tab.title}`);
							pass = this.checkFormComponents(tab.components, map, path);
							_.remove(path, p => p === label);
						}
					}
				}
			}
		}

		return pass;
	}

	async delete() {
		const modal = new ConfirmDeleteModal(this.$injector);
		const answer = await modal.open(this.form?.getName() || '');

		if (answer && this.kid) {
			await APIV5.instance.objectDelete({ items: this.kid }, { id: 'form-builder.delete' });
			this.modified = false;
			this.$location.path('/form-builder');
		}
	}

	duplicate() {
		this.#oldFormComponents = {};
		this.#oldFormName = undefined;
		this.#oldFormTerm = null;
		this.create = true;
		this.kid = `K_NODE;${this.User.getRef()}`;
		this.class_kid = this.$routeParams.classId;
		this.modified = true;
		this.loadForm(this.$routeParams.formId);
	}

	export() {
		if (this.form) {
			Utils.download(
				new Blob([this.form.export()]),
				'application/json',
				`export-${this.form.getName()}-${moment.utc().format('YYYYMMDDHHmmss')}.json`,
			);
		}
	}

	/**
	 * @param {Blob} file
	 */
	import(file) {
		const reader = new FileReader();
		reader.addEventListener('load', () => {
			try {
				const newForm = JSON.parse(reader.result?.toString() || '');
				this.$timeout(() => {
					_.extend(this.form, newForm);
				});
			}
			catch (/** @type {any} */ error) {
				this.Notification.error({
					title:   this.$translate.instant('error.title'),
					message: error.message,
				});
			}
		});
		// eslint-disable-next-line unicorn/prefer-blob-reading-methods
		reader.readAsText(file);
	}

	async loadClassInfo() {
		if (this.class_kid) {
			this.form = new Form(this.$injector);

			const namePrefixExists = await APIV5.instance.namePrefixExists({
				class_kid: this.class_kid,
			}, {
				id: 'form-builder.load-class-info',
			});

			if (namePrefixExists.result === true) {
				const result = await APIV5.instance.namePrefixGet(
					{
						isa:       this.class_kid,
						suffixe:   '',
						item_info: {
							links: {
								CONTEXT: [],
								TEMPS:   Utils.isoDate(),
							},
						},
					},
					{
						id: 'form-builder.load-class-info',
					},
				);
				this.form.prefix = result ? result.prefix : undefined;
			}

			this.class = await this.NodeService.load(this.class_kid);
			if (this.class instanceof Node) {
				this.$rootScope.formBuilderClassName = this.class.getName();
				if (!this.form.name) {
					this.form.name = this.class.getName();
				}
				this.searchBarMfOptions.name = this.form?.name ?? '';
			}
		}
	}

	/**
	 * @param {import('@knitiv/api-client-javascript').KNode} formKid
	 */
	async loadForm(formKid) {
		const node = await this.NodeService.load(formKid);
		if (node) {
			this.class_kid = node.getLink(COMPOSED_OF, 'K_NODE;SYS_CLASSE')?.objnum2_kid;
			await this.loadClassInfo();

			await this.$timeout(() => {
				this.form = new Form(this.$injector, {
					...node.getAttribute('KFORM').value,
					default: node.getLink(
						COMPOSED_OF,
						'K_NODE;BOOL',
						'K_NODE;DEFAULT',
					)?.objnum2_kid === 'K_NODE;TRUE' ?? false,
					term: node.getLink(COMPOSED_OF, 'K_NODE;TERM')?.objnum2_kid,
				});
				if (this.form && 'map' in this.form) {
					delete this.form.map;
				}
				this.#oldFormName = this.form?.getName();
				this.#oldFormTerm = this.form?.term ?? null;
				this.#oldFormComponents = { ...this.form?.components };
				this.modified = false;
			}, 100);
		}
	}

	/**
	 * @param {import('@knitiv/api-client-javascript').AutocompleteItem | undefined} item
	 * @returns {Promise<any>}
	 */
	async openForm(item) {
		if (!item) {
			this.$location.path(`/form-builder`);
			return
		}
		try {
			const data = await APIV5.instance.formList({ node_kid: item.kid }, { id: 'form-builder.open-form' });
			this.forms = data.result;

			const form = await this.$uibModal.open({
				ariaLabelledBy:  'modal-form-builder-select-title',
				ariaDescribedBy: 'modal-form-builder-select-body',
				template:        templateModalFormBuilderSelect,
				controller:      ModalFormBuilderSelectController,
				controllerAs:    '$ctrl',
				resolve:         {
					forms:    () => this.forms,
					classKid: () => item.kid,
				},
			}).result;

			if (form) {
				if ('generic' in form && form.generic) {
					this.$location.path(`/form-builder/new/${item.kid}`);
					this.$location.search('generic');
				}
				else if ('kid' in form) {
					this.$location.path(`/form-builder/${form.kid}`);
				}
			}
			else {
				this.$location.path(`/form-builder/new/${item.kid}`);
			}
			return form;
		}
		catch (error) {
			if (error !== 'cancel') {
				console.error(error);
			}
		}
	}

	async save() {
		if (!this.checkFormComponents(this.form?.components)) {
			return;
		}

		if (this.kid !== undefined) {
			/** @type {any} */
			const items = [
				{
					target_objnum: this.kid,
					OBJECT_LIST:   {},
				},
			];

			items[0].OBJECT_LIST[this.kid] = {
				isa:           'K_NODE;KFORM',
				REPRE_LIST:    [
					{
						name: this.form?.name,
						lang: 'K_NODE;UNIVERSEL',
					},
				],
				ATTRIBUT_LIST: [
					{
						name:     'KFORM',
						ref_type: 'KEPLER_TYPE;JSON',
						data1:    this.form?.export(),
					},
				],
				LINK_LIST:     {
					[COMPOSED_OF]: [
						{
							objnum2:    this.class?.kid,
							class_kid:  'K_NODE;SYS_CLASSE',
							linker_kid: '0',
						},
						{
							objnum2:    this.form?.default ? 'K_NODE;TRUE' : 'K_NODE;FALSE',
							class_kid:  'K_NODE;BOOL',
							linker_kid: 'K_NODE;DEFAULT',
						},
					],
				},
			};

			if (this.form?.term) {
				items[0].OBJECT_LIST[this.kid].LINK_LIST[COMPOSED_OF].push({
					objnum2:    this.form?.term,
					class_kid:  'K_NODE;TERM',
					linker_kid: '0',
				});
			}
			else if (this.#oldFormTerm) {
				items[0].OBJECT_LIST[this.kid].LINK_LIST[COMPOSED_OF].push({
					action:     'DEL',
					objnum2:    this.#oldFormTerm,
					class_kid:  'K_NODE;TERM',
					linker_kid: '0',
				});
			}

			const result = await APIV5.instance.objectSet({ items }, { id: 'form-builder.save' });
			this.Notification.success({
				title:   this.$translate.instant('object.successfully-updated-title'),
				message: this.$translate.instant('object.successfully-updated-message'),
			});
			this.#oldFormName = this.form?.name;
			this.#oldFormTerm = this.form?.term ?? null;
			this.#oldFormComponents = { ...this.form?.components };
			this.modified = false;
			this.create = false;
			// @ts-ignore
			this.kid = Object.keys(result.result)[0];
			this.$location.path(`/form-builder/${this.kid}`);
			this.$location.search({});
		}
	}

	showRef() {
		this.$uibModal.open({
			ariaLabelledBy:             'modal-form-builder-ref-title',
			ariaDescribedBy:            'modal-form-builder-ref-body',
			template:                   templateModalFormBuilderReference,
			controller: /* @ngInject */ (/** @type {any} */ $scope, /** @type {any} */ KformComponents) => {
				$scope.components = KformComponents.components;
				$scope.jsonEditorOptions = { mode: 'view' };
				$scope.component = $scope.components[Object.keys($scope.components)[0]];
				/** @type {any} */
				const settings = {};
				for (const [key, component] of Object.entries($scope.components)) {
					settings[key] = component.settings;
				}

				const exportComponentsDefinition = () => {
					// eslint-disable-next-line no-console
					console.log(angular.toJson(sortByKeys(settings)));
				};

				exportComponentsDefinition();
			},
			// controllerAs: '$ctrl',
		});
	}
}
